import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface ServiceDetailProcessProps {
  data: {
    title: string;
    steps: Array<{
      number: string;
      title: string;
      description: string;
    }>;
  };
}

const ServiceDetailProcess = ({ data }: ServiceDetailProcessProps) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section ref={ref} className="py-16 bg-white">
      <div className="max-w-[1440px] mx-auto px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className="font-poppins font-bold text-2xl lg:text-3xl text-gray-900 mb-3">
            {data.title}
          </h2>
        </motion.div>

        {/* Process Steps */}
        <div className="max-w-5xl mx-auto">
          {data.steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative flex items-start gap-6 mb-8 last:mb-0"
            >
              {/* Number Badge */}
              <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#007dff] to-[#0066dd] text-white rounded-xl font-poppins font-bold text-lg shadow-lg">
                {step.number}
              </div>

              {/* Content */}
              <div className="flex-1 bg-gradient-to-br from-[#007dff]/5 to-[#007dff]/10 rounded-xl p-5">
                <h3 className="font-poppins font-semibold text-base text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="font-poppins text-[13px] text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Connector Line */}
              {index < data.steps.length - 1 && (
                <div className="absolute left-7 top-14 w-0.5 h-8 bg-gradient-to-b from-[#007dff] to-[#007dff]/20"></div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceDetailProcess;
